import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { idbSaveJob, idbDeleteJob, idbListJobs } from '../utils/indexedDb';
import axiosInstance from '../services/axiosInstance';
import { getUploadErrorMessage } from '../utils/uploadUi';

export type UploadFileItem = {
  id: string;
  file: File;
  metadata: Record<string, any>;
};

export type BackgroundUpload = {
  id: string;
  title: string;
  files: UploadFileItem[];
  status: 'queued' | 'running' | 'paused' | 'completed' | 'failed' | 'cancelled';
  overallProgress: number; // 0-100
  successCount: number;
  failedCount: number;
  createdAt: number;
  nextIndex: number; // index of next file to process
  params: {
    selectedTemplate: { fields: Array<{ id: string; name: string }> } | null;
    selectedMainCategoryId: string;
    selectedSubcategoryIds: string[];
    applyToAllFiles: boolean;
  };
  // throughput info (optional)
  totalBytes?: number;
  uploadedBytes?: number;
  speedBps?: number;
  etaSeconds?: number;
  phase?: 'uploading' | 'processing';
  currentFileName?: string;
  currentFileIndex?: number;
  currentFileProgress?: number;
  currentFileSize?: number;
  lastError?: string;
  uploadedAssetIds: string[];
  uploadedCategoryIds: string[];
};

type EnqueueParams = {
  title: string;
  files: UploadFileItem[];
  selectedTemplate: { fields: Array<{ id: string; name: string }> } | null;
  selectedMainCategoryId: string;
  selectedSubcategoryIds: string[];
  applyToAllFiles: boolean;
};

type UploadProgressSnapshot = {
  uploadedAssetIds?: string[];
  uploadedCategoryIds?: string[];
  successCount?: number;
  failedCount?: number;
};

type UploadManagerContextType = {
  uploads: BackgroundUpload[];
  activeCount: number;
  enqueueBackgroundUpload: (params: EnqueueParams) => string;
  pauseUpload: (jobId: string) => void;
  resumeUpload: (jobId: string) => void;
  cancelUpload: (jobId: string) => void;
};

const UploadManagerContext = createContext<UploadManagerContextType | undefined>(undefined);

export const useUploadManager = (): UploadManagerContextType => {
  const ctx = useContext(UploadManagerContext);
  if (!ctx) throw new Error('useUploadManager must be used within UploadManagerProvider');
  return ctx;
};

export const UploadManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [uploads, setUploads] = useState<BackgroundUpload[]>([]);
  const controllersRef = useRef<Record<string, AbortController>>({});
  const pausedRef = useRef<Record<string, boolean>>({});
  const cancelledRef = useRef<Record<string, boolean>>({});
  const resumedOnceRef = useRef(false);
  const startingQueuedRef = useRef<Set<string>>(new Set());
  const lastUploadEventRef = useRef<number>(0);

  const startUpload = useCallback(async (
    jobId: string,
    params: EnqueueParams,
    startIndex: number = 0,
    progressSnapshot?: UploadProgressSnapshot,
  ) => {
    // Prepare totals and timers
    const totalBytes = params.files.reduce((sum, f) => sum + (f.file?.size || 0), 0);
    const startTime = Date.now();
    let completedBytes = params.files.slice(0, startIndex).reduce((sum, f) => sum + (f.file?.size || 0), 0);
    const uploadedAssetIds = new Set((progressSnapshot?.uploadedAssetIds || []).map(String));
    const uploadedCategoryIds = new Set((progressSnapshot?.uploadedCategoryIds || []).map(String));
    const initialFile = params.files[startIndex];

    setUploads(prev => prev.map(u => (u.id === jobId ? {
      ...u,
      status: 'running',
      totalBytes,
      uploadedBytes: completedBytes,
      speedBps: 0,
      etaSeconds: undefined,
      phase: initialFile ? 'uploading' : undefined,
      currentFileName: initialFile?.file?.name,
      currentFileIndex: startIndex,
      currentFileProgress: 0,
      currentFileSize: initialFile?.file?.size || 0,
      lastError: undefined,
    } : u)));

    let successCount = progressSnapshot?.successCount || 0;
    let failedCount = progressSnapshot?.failedCount || 0;
    let interrupted = false;

    for (let i = startIndex; i < params.files.length; i++) {
      const fileItem = params.files[i];
      const fileSize = fileItem.file?.size || 0;
      const completedBytesBeforeFile = completedBytes;
      try {
        setUploads(prev => prev.map(u => (u.id === jobId ? {
          ...u,
          currentFileName: fileItem.file?.name,
          currentFileIndex: i,
          currentFileProgress: 0,
          currentFileSize: fileSize,
          phase: 'uploading',
          speedBps: 0,
          etaSeconds: undefined,
          lastError: undefined,
        } : u)));

        const formData = new FormData();
        formData.append('asset', fileItem.file);
        // Idempotency: stable per-file key so backend can dedupe retries (refresh/resume)
        if (fileItem.id) formData.append('clientUploadId', fileItem.id);

        const metadata = fileItem.metadata || {};
        if (metadata.title) formData.append('title', metadata.title);
        if (metadata.description) formData.append('description', metadata.description);
        if (metadata.tags) {
          const tags = Array.isArray(metadata.tags) ? metadata.tags.join(',') : metadata.tags;
          formData.append('tags', tags);
        }
        if (Object.prototype.hasOwnProperty.call(metadata, 'skipValidooSync')) {
          formData.append('skipValidooSync', String(!!(metadata as any).skipValidooSync));
        }
        if (params.selectedMainCategoryId) {
          formData.append('categories', params.selectedMainCategoryId);
        }
        const fileSubcategories = params.applyToAllFiles
          ? params.selectedSubcategoryIds
          : (metadata.subcategoryIds || []);
        if (fileSubcategories && fileSubcategories.length > 0) {
          fileSubcategories.forEach((catId: string) => formData.append('categories', catId));
        }

        // Custom template metadata
        if (params.selectedTemplate) {
          const appendedMetadataKeys = new Set<string>();
          const appendMetadataField = (metadataField: string, value: any) => {
            if (appendedMetadataKeys.has(metadataField)) return;
            appendedMetadataKeys.add(metadataField);
            if (typeof value === 'object' && !(value instanceof Date)) {
              formData.append(metadataField, JSON.stringify(value));
            } else if (value instanceof Date) {
              formData.append(metadataField, value.toISOString());
            } else {
              formData.append(metadataField, String(value));
            }
          };

          Object.entries(metadata).forEach(([key, value]) => {
            if (['title', 'description', 'tags', 'subcategoryIds', 'skipValidooSync'].includes(key)) return;
            if (value === undefined || value === null) return;

            const templateField = params.selectedTemplate!.fields.find(f => f.id === key);
            const metadataField = `metadata.${key}`;
            if (templateField && templateField.name !== key) {
              const nameMetadataField = `metadata.${templateField.name}`;
              appendMetadataField(nameMetadataField, value);
            }
            appendMetadataField(metadataField, value);
          });
        }

        const controller = new AbortController();
        controllersRef.current[jobId] = controller;

        const response = await axiosInstance.post('/api/assets', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          signal: controller.signal,
          onUploadProgress: (evt) => {
            if (evt.total) {
              const fileProgress = Math.round((evt.loaded * 100) / evt.total);
              const uploadedNow = completedBytesBeforeFile + evt.loaded;
              const overall = totalBytes > 0 ? Math.round((uploadedNow * 100) / totalBytes) : 0;
              const elapsedMs = Math.max(1, Date.now() - startTime);
              const speedBps = (uploadedNow * 1000) / elapsedMs;
              const remaining = Math.max(0, totalBytes - uploadedNow);
              const etaSeconds = speedBps > 0 ? Math.round(remaining / speedBps) : undefined;
              const phase = evt.loaded >= evt.total ? 'processing' : 'uploading';
              setUploads(prev => prev.map(u => (u.id === jobId ? {
                ...u,
                overallProgress: overall,
                nextIndex: i,
                uploadedBytes: uploadedNow,
                speedBps,
                etaSeconds,
                phase,
                currentFileName: fileItem.file?.name,
                currentFileIndex: i,
                currentFileProgress: fileProgress,
                currentFileSize: fileSize,
              } : u)));
            }
          }
        });

        const uploadedAssets = Array.isArray(response?.data) ? response.data : [response?.data];
        uploadedAssets.forEach((asset: any) => {
          if (asset?._id) {
            uploadedAssetIds.add(String(asset._id));
          }
          if (Array.isArray(asset?.categories)) {
            asset.categories.forEach((categoryId: any) => {
              if (categoryId) {
                uploadedCategoryIds.add(String(categoryId));
              }
            });
          }
        });

        successCount++;
        // Update per-file counters immediately
        setUploads(prev => prev.map(u => (u.id === jobId ? {
          ...u,
          successCount: (u.successCount || 0) + 1,
          uploadedAssetIds: Array.from(uploadedAssetIds),
          uploadedCategoryIds: Array.from(uploadedCategoryIds),
          nextIndex: i + 1,
          uploadedBytes: completedBytesBeforeFile + fileSize,
          overallProgress: totalBytes > 0 ? Math.round(((completedBytesBeforeFile + fileSize) * 100) / totalBytes) : 100,
          currentFileProgress: 100,
          phase: 'processing',
        } : u)));
        // Persist progress for resume — write every 5 files to reduce IDB I/O (O(N²) → O(N/5))
        const isLastFile = i === params.files.length - 1;
        if ((i + 1) % 5 === 0 || isLastFile) {
          try {
            await idbSaveJob({
              id: jobId,
              params,
              files: params.files,
              nextIndex: i + 1,
              status: 'running',
              uploadedAssetIds: Array.from(uploadedAssetIds),
              uploadedCategoryIds: Array.from(uploadedCategoryIds),
              successCount,
              failedCount,
            });
          } catch {}
        }
        // Advance completed byte counter
        completedBytes += fileSize;
        // Inform app that a new asset was created — throttle to max once per 5s for large batches
        try {
          const now = Date.now();
          const isLargeBatch = params.files.length >= 5;
          if (!isLargeBatch || now - lastUploadEventRef.current >= 5000) {
            window.dispatchEvent(new CustomEvent('dam:asset-uploaded'));
            lastUploadEventRef.current = now;
          }
        } catch {}
      } catch (err) {
        const paused = !!pausedRef.current[jobId];
        const cancelled = !!cancelledRef.current[jobId];
        if (paused || cancelled) {
          interrupted = true;
          break;
        } else {
          failedCount++;
          const lastError = getUploadErrorMessage(err);
          setUploads(prev => prev.map(u => (u.id === jobId ? {
            ...u,
            failedCount: (u.failedCount || 0) + 1,
            uploadedAssetIds: Array.from(uploadedAssetIds),
            uploadedCategoryIds: Array.from(uploadedCategoryIds),
            nextIndex: i + 1,
            phase: undefined,
            currentFileName: fileItem.file?.name,
            currentFileIndex: i,
            currentFileProgress: 0,
            currentFileSize: fileSize,
            speedBps: 0,
            etaSeconds: undefined,
            lastError,
          } : u)));
        }
      }
    }

    if (cancelledRef.current[jobId]) {
      setUploads(prev => prev.map(u => (u.id === jobId ? { ...u, status: 'cancelled', phase: undefined, speedBps: 0, etaSeconds: undefined } : u)));
      try { await idbDeleteJob(jobId); } catch {}
      return;
    }

    if (pausedRef.current[jobId] || interrupted) {
      setUploads(prev => prev.map(u => (u.id === jobId ? { ...u, status: 'paused', phase: undefined, speedBps: 0, etaSeconds: undefined } : u)));
      // keep job in IDB with current nextIndex
      return;
    }

    setUploads(prev => prev.map(u => (u.id === jobId ? {
      ...u,
      // successCount/failedCount already incremented per file; avoid double-counting here
      status: (u.failedCount || 0) > 0 && (u.successCount || 0) === 0 ? 'failed' : 'completed',
      overallProgress: 100,
      nextIndex: params.files.length,
      uploadedBytes: totalBytes,
      speedBps: 0,
      etaSeconds: 0,
      phase: undefined,
      currentFileProgress: 100,
      currentFileIndex: params.files.length > 0 ? params.files.length - 1 : 0,
      currentFileName: params.files.length > 0 ? params.files[params.files.length - 1].file?.name : undefined,
      currentFileSize: params.files.length > 0 ? params.files[params.files.length - 1].file?.size || 0 : 0,
    } : u)));

    // Notify completion (let app refetch once at the end too)
    try {
      window.dispatchEvent(new CustomEvent('dam:upload-batch-completed', {
        detail: {
          assetIds: Array.from(uploadedAssetIds),
          categoryIds: Array.from(uploadedCategoryIds),
        },
      }));
    } catch {}
    // Remove from persistence on complete
    try { await idbDeleteJob(jobId); } catch {}
  }, []);

  // Limit active jobs; queue extra jobs
  const MAX_ACTIVE_JOBS = 2;
  const enqueueBackgroundUpload = useCallback((params: EnqueueParams) => {
    const id = `bg-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
    const job: BackgroundUpload = {
      id,
      title: params.title || 'Bulk upload',
      files: params.files,
      status: 'queued',
      overallProgress: 0,
      successCount: 0,
      failedCount: 0,
      createdAt: Date.now(),
      nextIndex: 0,
      phase: undefined,
      currentFileName: params.files[0]?.file?.name,
      currentFileIndex: 0,
      currentFileProgress: 0,
      currentFileSize: params.files[0]?.file?.size || 0,
      uploadedAssetIds: [],
      uploadedCategoryIds: [],
      params: {
        selectedTemplate: params.selectedTemplate,
        selectedMainCategoryId: params.selectedMainCategoryId,
        selectedSubcategoryIds: params.selectedSubcategoryIds,
        applyToAllFiles: params.applyToAllFiles
      }
    };
    setUploads(prev => [job, ...prev]);
    // Auto-open Upload Center so users see the queue
    try {
      window.dispatchEvent(new CustomEvent('dam:open-upload-center'));
    } catch {}
    // Persist job so we can resume after refresh
    idbSaveJob({
      id,
      params,
      files: params.files,
      nextIndex: 0,
      status: 'queued',
      uploadedAssetIds: [],
      uploadedCategoryIds: [],
      successCount: 0,
      failedCount: 0,
    });
    return id;
  }, []);

  // Auto-start queued jobs when slots are available (FIFO by createdAt)
  React.useEffect(() => {
    try {
      // Cleanup: remove ids that are no longer queued
      const queuedIds = new Set(uploads.filter(u => u.status === 'queued').map(u => u.id));
      for (const id of Array.from(startingQueuedRef.current)) {
        if (!queuedIds.has(id)) startingQueuedRef.current.delete(id);
      }
    } catch {}

    const runningCount = uploads.filter(u => u.status === 'running').length;
    let slots = MAX_ACTIVE_JOBS - runningCount;
    if (slots <= 0) return;

    const queued = uploads
      .filter(u => u.status === 'queued')
      .slice()
      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

    for (const job of queued) {
      if (slots <= 0) break;
      if (startingQueuedRef.current.has(job.id)) continue;

      startingQueuedRef.current.add(job.id);
      pausedRef.current[job.id] = false;
      cancelledRef.current[job.id] = false;

      const params: EnqueueParams = {
        title: job.title,
        files: job.files,
        selectedTemplate: job.params.selectedTemplate,
        selectedMainCategoryId: job.params.selectedMainCategoryId,
        selectedSubcategoryIds: job.params.selectedSubcategoryIds,
        applyToAllFiles: job.params.applyToAllFiles
      };

      startUpload(job.id, params, job.nextIndex || 0, {
        uploadedAssetIds: job.uploadedAssetIds,
        uploadedCategoryIds: job.uploadedCategoryIds,
        successCount: job.successCount,
        failedCount: job.failedCount,
      });
      slots--;
    }
  }, [uploads, startUpload]);

  const value = useMemo(() => ({
    uploads,
    activeCount: uploads.filter(u => u.status === 'queued' || u.status === 'running').length,
    enqueueBackgroundUpload,
    pauseUpload: (jobId: string) => {
      pausedRef.current[jobId] = true;
      const ctrl = controllersRef.current[jobId];
      if (ctrl) ctrl.abort();
      setUploads(prev => prev.map(u => (u.id === jobId ? { ...u, status: 'paused', phase: undefined, speedBps: 0, etaSeconds: undefined } : u)));
      // Persist paused state so refresh doesn't auto-resume
      try {
        const job = uploads.find(u => u.id === jobId);
        if (job) {
          idbSaveJob({
            id: jobId,
            params: { ...job.params, title: job.title },
            files: job.files,
            nextIndex: job.nextIndex || 0,
            status: 'paused',
            uploadedAssetIds: job.uploadedAssetIds,
            uploadedCategoryIds: job.uploadedCategoryIds,
            successCount: job.successCount,
            failedCount: job.failedCount,
          });
        }
      } catch {}
    },
    resumeUpload: (jobId: string) => {
      const job = uploads.find(u => u.id === jobId);
      if (!job) return;
      // Respect global concurrency
      const active = uploads.filter(u => u.status === 'running').length;
      if (active >= MAX_ACTIVE_JOBS) {
        setUploads(prev => prev.map(u => (u.id === jobId ? { ...u, status: 'queued' } : u)));
        return;
      }
      pausedRef.current[jobId] = false;
      cancelledRef.current[jobId] = false;
      try {
        idbSaveJob({
          id: jobId,
          params: { ...job.params, title: job.title },
          files: job.files,
          nextIndex: job.nextIndex || 0,
          status: 'queued',
          uploadedAssetIds: job.uploadedAssetIds,
          uploadedCategoryIds: job.uploadedCategoryIds,
          successCount: job.successCount,
          failedCount: job.failedCount,
        });
      } catch {}
      const params: EnqueueParams = {
        title: job.title,
        files: job.files,
        selectedTemplate: job.params.selectedTemplate,
        selectedMainCategoryId: job.params.selectedMainCategoryId,
        selectedSubcategoryIds: job.params.selectedSubcategoryIds,
        applyToAllFiles: job.params.applyToAllFiles
      } as EnqueueParams;
      startUpload(jobId, params, job.nextIndex || 0, {
        uploadedAssetIds: job.uploadedAssetIds,
        uploadedCategoryIds: job.uploadedCategoryIds,
        successCount: job.successCount,
        failedCount: job.failedCount,
      });
    },
    cancelUpload: async (jobId: string) => {
      cancelledRef.current[jobId] = true;
      const ctrl = controllersRef.current[jobId];
      if (ctrl) ctrl.abort();
      setUploads(prev => prev.map(u => (u.id === jobId ? { ...u, status: 'cancelled', phase: undefined, speedBps: 0, etaSeconds: undefined } : u)));
      try { await idbDeleteJob(jobId); } catch {}
    }
  }), [uploads, enqueueBackgroundUpload, startUpload]);

  // Warn user if leaving page while uploads are active
  React.useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      const active = uploads.some(u => u.status === 'queued' || u.status === 'running');
      if (active) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploads]);

  // Resume queued/running jobs after refresh
  React.useEffect(() => {
    if (resumedOnceRef.current) return;
    resumedOnceRef.current = true;
    (async () => {
      try {
        const stored = await idbListJobs();
        if (!stored || stored.length === 0) return;
        for (const item of stored) {
          const { id, params, files, nextIndex } = item;
          const persistedStatus = item?.status as string | undefined;
          const status: BackgroundUpload['status'] = (persistedStatus === 'paused') ? 'paused' : 'queued';
          setUploads(prev => [{
            id,
            title: params.title || 'Bulk upload',
            files: files,
            status,
            overallProgress: 0,
            createdAt: Date.now(),
            nextIndex: nextIndex || 0,
            uploadedAssetIds: Array.isArray(item?.uploadedAssetIds) ? item.uploadedAssetIds.map(String) : [],
            uploadedCategoryIds: Array.isArray(item?.uploadedCategoryIds) ? item.uploadedCategoryIds.map(String) : [],
            successCount: Number(item?.successCount) || 0,
            failedCount: Number(item?.failedCount) || 0,
            params: {
              selectedTemplate: params.selectedTemplate,
              selectedMainCategoryId: params.selectedMainCategoryId,
              selectedSubcategoryIds: params.selectedSubcategoryIds,
              applyToAllFiles: params.applyToAllFiles
            }
          }, ...prev]);
        }
      } catch {}
    })();
  }, []);

  return (
    <UploadManagerContext.Provider value={value}>
      {children}
    </UploadManagerContext.Provider>
  );
};

export default UploadManagerContext;


